import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Paper,
  Container,
  Grid,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

import {
  itemOptions as initialItemOptions,
  stationOptions as initialStationOptions,
} from "./Options";
import "../styles-quality-control/qa_style.css";
import { toast } from "react-toastify";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import CaptureChassisNo from "../ChassisNoFromCamera/ChassisNoFromCamera";
import ModelName from "../modelName/modelName";
import { useLocation } from "react-router-dom";
import { useChassis } from "../ChassisNoProvider/ChassisProvider";

interface TableRowData {
  no: number;
  item: string;
  station: string;
  description: string;
  reworkCompleted: string;
  verified: string;
}

const SVIChecklist: React.FC = () => {
  const axiosPrivate = useAxiosPrivate();
  const [rows, setRows] = useState<TableRowData[]>([]);
  const [itemOptions, setItemOptions] = useState<string[]>([
    ...initialItemOptions,
    "Add New",
  ]);
  const [stationOptions, setStationOptions] = useState<string[]>([
    ...initialStationOptions,
    "Add New",
  ]);
  const { chassisNo, setChassisNo, canAutoSetChassisNo } = useChassis();
  const [localChassisNo, setLocalChassisNo] = useState<string>(chassisNo || "");
  const [model, setModel] = useState<string>("");
  const [date, setDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );
  const [checkedBy, setCheckedBy] = useState<string>("");
  const [, setLoading] = useState<boolean>(false);
  const [addingNewItem, setAddingNewItem] = useState<number | null>(null);
  const [addingNewStation, setAddingNewStation] = useState<number | null>(null);
  const [newItem, setNewItem] = useState<string>("");
  const [newStation, setNewStation] = useState<string>("");
  const hasUnsavedChanges = chassisNo;
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryChassisNo = params.get("chassisNo");
    if (queryChassisNo && canAutoSetChassisNo) {
      setChassisNo(queryChassisNo);
      setLocalChassisNo(queryChassisNo);
    } else if (!canAutoSetChassisNo) {
      setChassisNo("");
      setLocalChassisNo("");
    }
  }, [location.search, setChassisNo, canAutoSetChassisNo]);

  const ensureDefaultRow = React.useCallback(() => {
    if (rows.length === 0) {
      setRows([
        {
          no: 1,
          item: "",
          station: "",
          description: "",
          reworkCompleted: "",
          verified: "",
        },
      ]);
    }
  }, [rows.length]);

  const prepareData = React.useCallback(
    async (chassis: string) => {
      if (!chassis) {
        setModel("");
        setCheckedBy("");
        setRows([]);
        return;
      }

      setLoading(true);
      try {
        const response = await axiosPrivate.post(
          `prepare_qc_svi_checklist/${chassis}`
        );
        const data = response.data;
        const fetchedItems = data.items?.map((item: any) => item.item) || [];
        const fetchedStations =
          data.items?.map((item: any) => item.station) || [];

        setItemOptions((prev) => {
          const mergedItems = Array.from(
            new Set([
              ...prev.filter((opt) => opt !== "Add New"),
              ...fetchedItems,
              "Add New",
            ])
          );
          return mergedItems;
        });

        setStationOptions((prev) => {
          const mergedStations = Array.from(
            new Set([
              ...prev.filter((opt) => opt !== "Add New"),
              ...fetchedStations,
              "Add New",
            ])
          );
          return mergedStations;
        });

        const fetchedRows = data.items || [];
        if (fetchedRows.length === 0) {
          fetchedRows.push({
            no: 1,
            item: "",
            station: "",
            description: "",
            reworkCompleted: "",
            verified: "",
          });
        }

        setRows(data.items || []);
        setModel(data.model || "");
        setCheckedBy(data.checkedBy || "");

        const formattedDate = data.createdOn
          ? new Date(data.createdOn).toLocaleDateString("en-CA", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })
          : "";
        setDate(formattedDate || "");
      } catch (error) {
        console.error("Error fetching data: ", error);
        toast.error("Failed to fetch data for the provided chassis number.");
      } finally {
        setLoading(false);
      }
    },
    [axiosPrivate]
  );

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);
  React.useEffect(() => {
    ensureDefaultRow();
  }, [ensureDefaultRow]);

  React.useEffect(() => {
    const delayDebounce = setTimeout(() => {
      prepareData(chassisNo);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [chassisNo, prepareData]);

  const addRow = () => {
    const newRow: TableRowData = {
      no: rows.length + 1,
      item: "",
      station: "",
      description: "",
      reworkCompleted: "",
      verified: "",
    };
    setRows([...rows, newRow]);
  };

  const handleInputChange = (
    no: number,
    field: keyof TableRowData,
    value: string
  ) => {
    const updatedRows = rows.map((row) =>
      row.no === no ? { ...row, [field]: value } : row
    );
    setRows(updatedRows);
  };

  const saveTable = async () => {
    if (!chassisNo.trim()) {
      toast.error("Chassis No is required.");
      return;
    }
    if (!date.trim()) {
      toast.error("Date is required.");
      return;
    }
    if (!checkedBy.trim()) {
      toast.error("Checked By field is required.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        chassisNo: chassisNo,
        model: model,
        items: rows.map((i) => ({
          no: i.no,
          item: i.item,
          station: i.station,
          description: i.description,
          reworkCompleted: i.reworkCompleted,
          verified: i.verified,
        })),
        checkedBy: checkedBy,
        createdOn: date,
      };

      await toast.promise(
        axiosPrivate.post("create_or_update_qa_svi_checklist", payload),
        {
          pending: "Request pending",
        }
      );
      toast.success("Request Success..! 👌");
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save data.");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckedByChange = (event: any) => {
    setCheckedBy(event.target.value);
  };

  const handleDropdownChange = (
    no: number,
    field: keyof TableRowData,
    value: string
  ) => {
    if (value === "Add New") {
      if (field === "item") setAddingNewItem(no);
      if (field === "station") setAddingNewStation(no);
    } else {
      handleInputChange(no, field, value);
    }
  };

  const handleAddNewItem = (no: number) => {
    if (newItem.trim()) {
      setItemOptions((prev) => {
        const updatedOptions = [
          ...prev.filter((opt) => opt !== "Add New"),
          newItem,
          "Add New",
        ];
        return updatedOptions;
      });
      handleInputChange(no, "item", newItem);
    }
    setAddingNewItem(null);
    setNewItem("");
  };

  const handleAddNewStation = (no: number) => {
    if (newStation.trim()) {
      setStationOptions((prev) => {
        const updatedOptions = [
          ...prev.filter((opt) => opt !== "Add New"),
          newStation,
          "Add New",
        ];
        return updatedOptions;
      });
      handleInputChange(no, "station", newStation);
    }
    setAddingNewStation(null);
    setNewStation("");
  };

  const handleUploadComplete = (data: { chassisNo: string }) => {
    setLocalChassisNo(data.chassisNo);
    if (canAutoSetChassisNo) {
      setChassisNo(data.chassisNo);
    }
  };

  return (
    <>
      <Container sx={{ mt: 2, mb: 3, bgcolor: "background.default" }}>
        <Typography variant="h4" gutterBottom className="mb-4">
          FINAL AUDIT
        </Typography>
        <Grid container spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Grid
            item
            md={3}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <div style={{ display: "flex", alignItems: "center" }}>
              <CaptureChassisNo onUploadComplete={handleUploadComplete} />
            </div>

            <TextField
              label="Chassis No"
              variant="filled"
              size="small"
              value={localChassisNo}
              onChange={(e) => setLocalChassisNo(e.target.value)}
              sx={{
                "& .MuiInputLabel-root": {
                  fontWeight: "bold",
                  color: "#1976d2 !important",
                },
                "& .MuiInputBase-input": {
                  color: "#1976d2 !important",
                  opacity: "1 !important",
                  "&[disabled]": {
                    color: "rgba(25, 118, 210, 1) !important",
                  },
                  fontSize: "20px",
                },
                flexGrow: 1,
                minWidth: "120px",
              }}
            />
          </Grid>

          <Grid item md={9} sx={{ display: "flex", alignItems: "center" }}>
            <ModelName model={model} />
          </Grid>
        </Grid>
        <Grid container mt={4}>
          <Grid item md={12}>
            <TableContainer component={Paper} style={{ marginBottom: "20px" }}>
              <Table>
                <TableHead>
                  <TableRow className="table__head_row">
                    <TableCell
                      className="table__head_cell"
                      sx={{ width: "5%" }}
                    >
                      No
                    </TableCell>
                    <TableCell
                      className="table__head_cell"
                      sx={{ width: "13%" }}
                    >
                      Item
                    </TableCell>
                    <TableCell
                      className="table__head_cell"
                      sx={{ width: "13.5%" }}
                    >
                      Station
                    </TableCell>
                    <TableCell
                      className="table__head_cell"
                      sx={{ width: "20%" }}
                    >
                      Description
                    </TableCell>
                    <TableCell
                      className="table__head_cell"
                      sx={{ width: "20%" }}
                    >
                      Rework Completed
                    </TableCell>
                    <TableCell
                      className="table__head_cell"
                      sx={{ width: "10%" }}
                    >
                      Verified
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.no}>
                      <TableCell
                        sx={{ padding: "5px 10px", textAlign: "center" }}
                      >
                        {row.no}
                      </TableCell>
                      <TableCell
                        sx={{ borderLeft: "1px solid #d0d0d0", padding: "5px" }}
                      >
                        {addingNewItem === row.no ? (
                          <TextField
                            placeholder="Enter new Item"
                            variant="standard"
                            value={newItem}
                            onChange={(e) => setNewItem(e.target.value)}
                            onBlur={() => handleAddNewItem(row.no)}
                            size="small"
                            fullWidth
                            autoFocus
                            InputProps={{
                              disableUnderline: true,
                              style: {
                                fontSize: "14px",
                              },
                            }}
                            sx={{
                              "& .MuiInputBase-input": {
                                padding: 0,
                              },
                              "& .MuiInputBase-root": {
                                border: "none",
                                backgroundColor: "transparent",
                              },
                            }}
                          />
                        ) : (
                          <Select
                            value={row.item}
                            onChange={(e) =>
                              handleDropdownChange(
                                row.no,
                                "item",
                                e.target.value as string
                              )
                            }
                            variant="standard"
                            fullWidth
                            displayEmpty
                            sx={{
                              "& .MuiSelect-select": {
                                padding: "8px",
                                fontSize: "14px",
                              },
                              "&:before, &:after": {
                                borderBottom: "none !important",
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                              backgroundColor: "transparent",
                            }}
                          >
                            {itemOptions.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </TableCell>
                      <TableCell
                        sx={{ borderLeft: "1px solid #d0d0d0", padding: "5px" }}
                      >
                        {addingNewStation === row.no ? (
                          <TextField
                            placeholder="Enter new station"
                            variant="standard"
                            value={newStation}
                            onChange={(e) => setNewStation(e.target.value)}
                            onBlur={() => handleAddNewStation(row.no)}
                            size="small"
                            fullWidth
                            autoFocus
                            InputProps={{
                              disableUnderline: true,
                              style: {
                                fontSize: "14px",
                              },
                            }}
                            sx={{
                              "& .MuiInputBase-input": {
                                padding: 0,
                              },
                              "& .MuiInputBase-root": {
                                border: "none",
                                backgroundColor: "transparent",
                              },
                            }}
                          />
                        ) : (
                          <Select
                            value={row.station}
                            onChange={(e) =>
                              handleDropdownChange(
                                row.no,
                                "station",
                                e.target.value as string
                              )
                            }
                            variant="standard"
                            fullWidth
                            displayEmpty
                            sx={{
                              "& .MuiSelect-select": {
                                padding: "8px",
                                fontSize: "14px",
                              },
                              "&:before, &:after": {
                                borderBottom: "none !important",
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                              backgroundColor: "transparent",
                            }}
                          >
                            {stationOptions.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </TableCell>

                      <TableCell
                        sx={{
                          borderLeft: "1px solid #d0d0d0",
                          padding: "5px",
                          width: "20%",
                          wordWrap: "break-word",
                          whiteSpace: "pre-wrap",
                          overflowWrap: "break-word",
                          textAlign: "left",
                        }}
                      >
                        <TextField
                          placeholder="Enter Description"
                          variant="standard"
                          value={row.description}
                          onChange={(e) =>
                            handleInputChange(
                              row.no,
                              "description",
                              e.target.value
                            )
                          }
                          size="small"
                          fullWidth
                          multiline={true}
                          minRows={1}
                          InputProps={{
                            disableUnderline: true,
                            style: { fontSize: "14px" },
                          }}
                          sx={{
                            "& .MuiInputBase-input": {
                              padding: "5px",
                            },
                            "& .MuiInputBase-root": {
                              border: "none",
                              backgroundColor: "transparent",
                            },
                          }}
                        />
                      </TableCell>

                      <TableCell
                        sx={{
                          borderLeft: "1px solid #d0d0d0",
                          padding: "5px",
                          width: "20%",
                          wordWrap: "break-word",
                          whiteSpace: "pre-wrap",
                          overflowWrap: "break-word",
                          textAlign: "left",
                        }}
                      >
                        <TextField
                          variant="standard"
                          placeholder="Enter Rework Completed"
                          value={row.reworkCompleted}
                          onChange={(e) =>
                            handleInputChange(
                              row.no,
                              "reworkCompleted",
                              e.target.value
                            )
                          }
                          size="small"
                          fullWidth
                          multiline={true}
                          minRows={1}
                          InputProps={{
                            disableUnderline: true,
                            style: { fontSize: "14px" },
                          }}
                          sx={{
                            "& .MuiInputBase-input": {
                              padding: "5px",
                            },
                            "& .MuiInputBase-root": {
                              border: "none",
                              backgroundColor: "transparent",
                            },
                          }}
                        />
                      </TableCell>

                      <TableCell
                        sx={{ borderLeft: "1px solid #d0d0d0", padding: "5px" }}
                      >
                        <TextField
                          variant="standard"
                          placeholder="Enter Verified Status"
                          value={row.verified}
                          onChange={(e) =>
                            handleInputChange(
                              row.no,
                              "verified",
                              e.target.value
                            )
                          }
                          InputProps={{
                            disableUnderline: true,
                            style: {
                              fontSize: "14px",
                            },
                          }}
                          fullWidth
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
        <Grid container spacing={3} my={4}>
          <Grid item md={4}>
            <TextField
              label="Checked by:"
              variant="outlined"
              value={checkedBy}
              onChange={handleCheckedByChange}
              fullWidth
            />
          </Grid>
          <Grid item md={4}>
            <TextField
              label="Date:"
              variant="outlined"
              type="date"
              value={date}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>
        <div style={{ display: "flex", gap: "10px" }}>
          <Button variant="contained" color="primary" onClick={addRow}>
            Add Row
          </Button>
          <Button variant="contained" color="secondary" onClick={saveTable}>
            Save
          </Button>
        </div>
      </Container>
    </>
  );
};

export default SVIChecklist;
